import {
    VStack,
    HStack,
    Flex,
    Button,
    Box,
    Text,
    Alert,
    AlertIcon,
    useToast,
    useDisclosure,
    AlertDialog,
    AlertDialogBody,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogContent,
    AlertDialogOverlay,
    UnorderedList,
    ListItem,
    Progress,
} from '@chakra-ui/react';
import { useState, useRef, useEffect, useContext, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import imageCompression from 'browser-image-compression';
import { useUploadInvoice } from '../../hooks/invoice/useUploadInvoice';
import {
    useBulkUploadInvoices,
    WEB_BULK_UPLOAD_MAX,
} from '../../hooks/invoice/useBulkUploadInvoices';
import axios from 'axios';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { InvoiceFileDropzone, InvoiceInboxAddressesTable, InvoiceUploadForm } from '../../components/invoice';
import type { InvoiceType } from '../../types/invoice';
import { getPdfPageCountFromFile, mergePdfFiles } from '../../utils/invoicePdfPages';
import {
    INVOICE_UPLOAD_ACCEPT,
    isAcceptedInvoiceUploadFile,
    isInvoiceImageFile,
    isPdfFile,
    isTiffFile,
} from '../../utils/invoiceUploadFiles';
import AppDrawer from '../../components/AppDrawer';
import { useGetSettings } from '../../hooks/useGetSettings';
import { readFullInvoicingSettingsFromSections } from '../../utils/invoicingSettingsApi';

interface CreateInvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const COMPRESSION_OPTIONS = {
    maxSizeMB: 1.5,
    maxWidthOrHeight: 1600,
    useWebWorker: true as const,
};
const INVOICE_UPLOAD_PROCESSING_TOAST_ID = 'invoice-upload-processing';

type UploadMode = 'single' | 'bulk' | 'email';

type BulkFileEntry = {
    id: string;
    file: File;
    previewUrl: string;
    kind: 'image' | 'pdf';
};

const CreateInvoiceModal = ({ isOpen, onClose }: CreateInvoiceModalProps) => {
    const [uploadMode, setUploadMode] = useState<UploadMode>('single');
    const [invoiceType, setInvoiceType] = useState<InvoiceType>('received');
    const [ccVendorId, setCcVendorId] = useState<number | null>(null);
    const [ccVendorName, setCcVendorName] = useState<string | null>(null);
    const [uploadedImages, setUploadedImages] = useState<Array<{ file: File; previewUrl: string; isTiff?: boolean; }>>([]);
    const [uploadedPdf, setUploadedPdf] = useState<{ file: File; previewUrl: string; } | null>(null);
    const [bulkFiles, setBulkFiles] = useState<BulkFileEntry[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const bulkFileInputRef = useRef<HTMLInputElement>(null);
    const toast = useToast();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const { data: settingsData } = useGetSettings();
    const [processingUuid, setProcessingUuid] = useState<string | null>(null);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const processingToastIdRef = useRef<string | number | null>(null);
    const multiImageConfirmRef = useRef<HTMLButtonElement>(null);

    const { isOpen: isMultiImageConfirmOpen, onOpen: onMultiImageConfirmOpen, onClose: onMultiImageConfirmClose } = useDisclosure();

    const uploadInvoiceMutation = useUploadInvoice();
    const bulkUploadMutation = useBulkUploadInvoices();

    const invoicingSettings = useMemo(
        () =>
            readFullInvoicingSettingsFromSections(
                settingsData?.topSection?.sections,
                settingsData?.bottomSection?.sections,
            ),
        [settingsData],
    );
    const emailInvoicingEnabled =
        invoicingSettings.emailInvoicingOptIn === true && !!invoicingSettings.invoiceInboxAddresses;
    const invoiceInboxAddresses = invoicingSettings.invoiceInboxAddresses;

    const isBusy = isUploading || uploadInvoiceMutation.isPending || bulkUploadMutation.isPending || isCompressing;

    useEffect(() => {
        if (!emailInvoicingEnabled && uploadMode === 'email') {
            setUploadMode('single');
        }
    }, [emailInvoicingEnabled, uploadMode]);

    // Poll for invoice processing completion (single upload)
    useEffect(() => {
        if (!processingUuid) {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
            return;
        }

        const checkInvoiceStatus = async () => {
            try {
                const { data } = await axios.post(
                    `${API_URL}/invoice/processing-status`,
                    { uuid: processingUuid },
                    { headers: { Authorization: sessionContext?.sessionId ?? '' } }
                );
                const status = data.status as string;
                if (status === 'quality_failed') {
                    setProcessingUuid(null);
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                    if (processingToastIdRef.current) {
                        toast.close(processingToastIdRef.current);
                        processingToastIdRef.current = null;
                    }
                    const isRoNotDetected = data.errorCode === 'RO_NOT_DETECTED';
                    toast({
                        title: isRoNotDetected ? 'RO number not detected' : 'Image quality issues',
                        description: isRoNotDetected
                            ? 'RO number could not be detected in the image.'
                            : (data.error ?? 'One or more images were too blurry, dark, or bright. Please try again with clearer images.'),
                        status: 'error',
                        duration: 6000,
                        isClosable: true,
                    });
                    return;
                }
                if (status === 'processing_complete') {
                    setProcessingUuid(null);
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }
                    if (processingToastIdRef.current) {
                        toast.close(processingToastIdRef.current);
                        processingToastIdRef.current = null;
                    }
                    const invoiceStatus = data.invoiceStatus as string | undefined;
                    const isFlagged = invoiceStatus === 'flagged';
                    toast({
                        title: 'Invoice processed successfully',
                        description: isFlagged
                            ? 'Invoice processed but needs review (flagged).'
                            : 'Invoice has been processed and is ready.',
                        status: isFlagged ? 'warning' : 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                    queryClient.invalidateQueries({ queryKey: ['/invoices/feed'] });
                    return;
                }
                const feedRes = await axios.post(
                    `${API_URL}/invoices/feed`,
                    { type: 'all', limit: 50, offset: 0, locationIds: 'all' },
                    { headers: { Authorization: sessionContext?.sessionId ?? '' } }
                );
                const recentInvoice = feedRes.data?.invoices?.[0];
                if (recentInvoice?.status && (recentInvoice.status === 'submitted' || recentInvoice.status === 'flagged' || recentInvoice.status === 'manually_submitted')) {
                    const invoiceTime = new Date(recentInvoice.timestamp || recentInvoice.timeUploaded);
                    const timeDiff = (Date.now() - invoiceTime.getTime()) / 1000;
                    if (timeDiff < 30) {
                        setProcessingUuid(null);
                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }
                        if (processingToastIdRef.current) {
                            toast.close(processingToastIdRef.current);
                            processingToastIdRef.current = null;
                        }
                        toast({
                            title: 'Invoice processed successfully',
                            description: recentInvoice.status === 'submitted' || recentInvoice.status === 'manually_submitted'
                                ? (recentInvoice.status === 'manually_submitted' ? 'Invoice was reviewed and saved (manually submitted).' : 'Invoice has been processed and is ready.')
                                : 'Invoice processed but needs review (flagged).',
                            status: recentInvoice.status === 'submitted' || recentInvoice.status === 'manually_submitted' ? 'success' : 'warning',
                            duration: 5000,
                            isClosable: true,
                        });
                        queryClient.invalidateQueries({ queryKey: ['/invoices/feed'] });
                    }
                }
            } catch {
                // Polling failed; will retry on next interval
            }
        };

        let pollCount = 0;
        pollingIntervalRef.current = setInterval(() => {
            pollCount++;
            if (pollCount >= 30) {
                setProcessingUuid(null);
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }

                if (processingToastIdRef.current) {
                    toast.close(processingToastIdRef.current);
                    processingToastIdRef.current = null;
                }

                toast({
                    title: 'Processing taking longer than expected',
                    description: 'Invoice is being processed. Please check back in a moment.',
                    status: 'info',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                checkInvoiceStatus();
            }
        }, 2000);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [processingUuid, sessionContext?.sessionId, toast, queryClient]);

    const processFiles = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const files = Array.from(fileList).filter(isAcceptedInvoiceUploadFile);
        if (files.length === 0) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload JPG, PNG, TIF images or a single PDF',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        const pdfFiles = files.filter(isPdfFile);
        const imageFiles = files.filter(isInvoiceImageFile);
        if (pdfFiles.length > 0 && imageFiles.length > 0) {
            toast({
                title: 'Mixed files not allowed',
                description: 'Upload either images only or a single PDF, not both.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (pdfFiles.length > 1) {
            setIsCompressing(true);
            try {
                let merged = pdfFiles[0]!;
                for (let i = 1; i < pdfFiles.length; i += 1) {
                    merged = await mergePdfFiles(merged, pdfFiles[i]!);
                }
                const pageCount = await getPdfPageCountFromFile(merged);
                setUploadedPdf(prev => {
                    if (prev) URL.revokeObjectURL(prev.previewUrl);
                    return { file: merged, previewUrl: URL.createObjectURL(merged), pageCount };
                });
                setUploadedImages(prev => {
                    prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
                    return [];
                });
            } catch (err) {
                console.error('Could not read or merge PDF file(s):', err);
                toast({
                    title: 'Could not read PDF',
                    description: 'Could not combine or read the selected PDF file(s). Try a single multi-page PDF.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setIsCompressing(false);
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        if (pdfFiles.length === 1) {
            setIsCompressing(true);
            try {
                const file = pdfFiles[0]!;
                const pageCount = await getPdfPageCountFromFile(file);
                setUploadedPdf(prev => {
                    if (prev) URL.revokeObjectURL(prev.previewUrl);
                    return { file, previewUrl: URL.createObjectURL(file), pageCount };
                });
                setUploadedImages(prev => {
                    prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
                    return [];
                });
            } catch (err) {
                console.error('Could not read PDF:', err);
                toast({
                    title: 'Could not read PDF',
                    description: 'The PDF could not be opened. Try re-saving or exporting the file.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setIsCompressing(false);
            }
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }
        setIsCompressing(true);
        try {
            const compressedWithPreviews = await Promise.all(
                imageFiles.map(async (file) => {
                    if (isTiffFile(file)) {
                        return { file, previewUrl: '', isTiff: true };
                    }
                    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
                    return { file: compressed, previewUrl: URL.createObjectURL(compressed), isTiff: false };
                })
            );
            setUploadedImages(prev => {
                prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
                return [...prev, ...compressedWithPreviews];
            });
            setUploadedPdf(prev => {
                if (prev) URL.revokeObjectURL(prev.previewUrl);
                return null;
            });
        } catch {
            toast({
                title: 'Compression failed',
                description: 'Could not process one or more images. Try different files.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsCompressing(false);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const processBulkFiles = async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const files = Array.from(fileList).filter(isAcceptedInvoiceUploadFile);
        if (files.length === 0) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload JPG, PNG, TIF, or PDF files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        const remaining = WEB_BULK_UPLOAD_MAX - bulkFiles.length;
        if (remaining <= 0) {
            toast({
                title: 'Limit reached',
                description: `You can upload at most ${WEB_BULK_UPLOAD_MAX} invoices at once.`,
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        const toAdd = files.slice(0, remaining);
        if (files.length > remaining) {
            toast({
                title: 'Limit reached',
                description: `Only the first ${remaining} file(s) were added (max ${WEB_BULK_UPLOAD_MAX}).`,
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
        }

        setIsCompressing(true);
        try {
            const nextEntries: BulkFileEntry[] = [];
            for (const file of toAdd) {
                if (isPdfFile(file)) {
                    nextEntries.push({
                        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        file,
                        previewUrl: URL.createObjectURL(file),
                        kind: 'pdf',
                    });
                } else if (isTiffFile(file)) {
                    nextEntries.push({
                        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        file,
                        previewUrl: '',
                        kind: 'image',
                    });
                } else {
                    const compressed = await imageCompression(file, COMPRESSION_OPTIONS);
                    nextEntries.push({
                        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        file: compressed,
                        previewUrl: URL.createObjectURL(compressed),
                        kind: 'image',
                    });
                }
            }
            setBulkFiles((prev) => [...prev, ...nextEntries]);
        } catch {
            toast({
                title: 'Could not process files',
                description: 'Try different files and try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsCompressing(false);
            if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        processFiles(event.target.files);
    };

    const handleBulkFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        processBulkFiles(event.target.files);
    };

    const removeImage = (index: number) => {
        setUploadedImages(prev => {
            const next = prev.filter((_, i) => i !== index);
            URL.revokeObjectURL(prev[index].previewUrl);
            return next;
        });
    };

    const removePdf = () => {
        setUploadedPdf(prev => {
            if (prev) URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
    };

    const removeBulkFile = (id: string) => {
        setBulkFiles((prev) => {
            const target = prev.find((f) => f.id === id);
            if (target) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((f) => f.id !== id);
        });
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (uploadMode === 'bulk') {
            processBulkFiles(e.dataTransfer.files);
        } else if (uploadMode === 'single') {
            processFiles(e.dataTransfer.files);
        }
    };

    const handleSubmit = async () => {
        const hasImages = uploadedImages.length > 0;
        const hasPdf = uploadedPdf !== null;
        if (!hasImages && !hasPdf) {
            toast({
                title: 'File required',
                description: 'Please upload at least one image or a PDF',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        if (uploadedImages.length > 1) {
            onMultiImageConfirmOpen();
            return;
        }

        performUpload();
    };

    const performUpload = useCallback(async () => {
        const hasPdf = uploadedPdf !== null;

        onMultiImageConfirmClose();
        setIsUploading(true);

        try {
            const result = await uploadInvoiceMutation.mutateAsync({
                ...(hasPdf ? { pdfFile: uploadedPdf!.file } : { imageFiles: uploadedImages.map(p => p.file) }),
                invoiceType,
                ...((invoiceType === 'received' || invoiceType === 'sublet') &&
                ccVendorId != null
                    ? {
                        ccVendorId,
                        ...(ccVendorName ? { ccVendorName } : {}),
                    }
                    : {}),
            });

            const processingToastId = toast({
                id: INVOICE_UPLOAD_PROCESSING_TOAST_ID,
                title: 'Invoice uploaded',
                description: 'Processing invoice...',
                status: 'info',
                duration: null,
                isClosable: true,
                onCloseComplete: () => {
                    processingToastIdRef.current = null;
                },
            });
            processingToastIdRef.current = processingToastId;

            setProcessingUuid(result.uuid);

            setInvoiceType('received');
            setCcVendorId(null);
            setCcVendorName(null);
            setUploadedImages(prev => {
                prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
                return [];
            });
            setUploadedPdf(prev => {
                if (prev) URL.revokeObjectURL(prev.previewUrl);
                return null;
            });
            onClose();

        } catch (error) {
            if (processingToastIdRef.current) {
                toast.close(processingToastIdRef.current);
                processingToastIdRef.current = null;
            }

            const message = error instanceof Error ? error.message : 'Failed to upload invoice. Please try again.';
            toast({
                title: 'Upload failed',
                description: message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsUploading(false);
        }
    }, [
        uploadedImages,
        uploadedPdf,
        invoiceType,
        ccVendorId,
        ccVendorName,
        uploadInvoiceMutation,
        toast,
        onClose,
        onMultiImageConfirmClose,
    ]);

    const performBulkUpload = async () => {
        if (bulkFiles.length === 0) {
            toast({
                title: 'Files required',
                description: 'Please select at least one invoice file',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            const result = await bulkUploadMutation.mutateAsync({
                files: bulkFiles.map((f) => f.file),
                invoiceType,
            });

            toast({
                title: 'Bulk upload started',
                description: `${result.accepted} invoice${result.accepted === 1 ? '' : 's'} queued for processing.`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            queryClient.invalidateQueries({ queryKey: ['/invoices/feed'] });

            setInvoiceType('received');
            setBulkFiles((prev) => {
                prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
                return [];
            });
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to bulk upload invoices.';
            toast({
                title: 'Bulk upload failed',
                description: message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const clearAllFiles = () => {
        setUploadedImages(prev => {
            prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
            return [];
        });
        setUploadedPdf(prev => {
            if (prev) URL.revokeObjectURL(prev.previewUrl);
            return null;
        });
        setBulkFiles(prev => {
            prev.forEach(p => URL.revokeObjectURL(p.previewUrl));
            return [];
        });
    };

    const handleClose = () => {
        if (!isBusy) {
            setInvoiceType('received');
            clearAllFiles();
            setUploadMode('single');
            onClose();
        }
    };

    const singleHasFiles = uploadedImages.length > 0 || uploadedPdf !== null;

    const modeOptions: Array<{ id: UploadMode; label: string }> = [
        { id: 'single', label: 'Single' },
        { id: 'bulk', label: 'Bulk' },
        ...(emailInvoicingEnabled ? [{ id: 'email' as const, label: 'Email' }] : []),
    ];

    const footerPrimary =
        uploadMode === 'single' ? (
            <Button
                size="action"
                bg="#0071E3"
                color="white"
                _hover={{ bg: '#3544a0' }}
                onClick={handleSubmit}
                isLoading={isUploading || uploadInvoiceMutation.isPending}
                loadingText="Uploading..."
                isDisabled={!singleHasFiles || isCompressing}
            >
                Create Invoice
            </Button>
        ) : uploadMode === 'bulk' ? (
            <Button
                size="action"
                bg="#0071E3"
                color="white"
                _hover={{ bg: '#3544a0' }}
                onClick={performBulkUpload}
                isLoading={bulkUploadMutation.isPending}
                loadingText={bulkUploadMutation.progress?.message ?? 'Uploading...'}
                isDisabled={bulkFiles.length === 0 || isCompressing}
            >
                Bulk Create
            </Button>
        ) : null;

    return (
        <>
            <AppDrawer
                isOpen={isOpen}
                onClose={handleClose}
                size="lg"
                title="Create Invoice"
                footer={
                    <>
                        <Button size="action" variant="outlineGray" onClick={handleClose} isDisabled={isBusy}>
                            Cancel
                        </Button>
                        {footerPrimary}
                    </>
                }
            >
                <VStack spacing={6} align="stretch">
                    <Flex
                        align="flex-end"
                        borderBottom="1.5px solid"
                        borderColor="gray.200"
                        gap={0}
                    >
                        {modeOptions.map((option) => {
                            const isSelected = uploadMode === option.id;
                            return (
                                <Box
                                    key={option.id}
                                    as="button"
                                    type="button"
                                    onClick={() => {
                                        if (!isBusy) setUploadMode(option.id);
                                    }}
                                    disabled={isBusy}
                                    fontWeight="medium"
                                    fontSize="sm"
                                    color={isSelected ? 'gray.800' : 'gray.500'}
                                    bg="transparent"
                                    border="none"
                                    borderBottom="1.5px solid"
                                    borderColor={isSelected ? 'gray.500' : 'transparent'}
                                    mb="-1.5px"
                                    py={2}
                                    px={3}
                                    cursor={isBusy ? 'not-allowed' : 'pointer'}
                                    opacity={isBusy ? 0.6 : 1}
                                    transition="all 0.15s"
                                    _hover={{
                                        color: isSelected ? 'gray.800' : 'gray.700',
                                    }}
                                >
                                    {option.label}
                                </Box>
                            );
                        })}
                    </Flex>

                    {uploadMode === 'single' && (
                        <VStack spacing={6} align="stretch">
                            <InvoiceUploadForm
                                invoiceType={invoiceType}
                                onInvoiceTypeChange={(value) => {
                                    setInvoiceType(value);
                                    if (value !== 'received' && value !== 'sublet') {
                                        setCcVendorId(null);
                                        setCcVendorName(null);
                                    }
                                }}
                                showCcVendorSelect
                                ccVendorId={ccVendorId}
                                onCcVendorChange={({ ccVendorId: id, ccVendorName: name }) => {
                                    setCcVendorId(id);
                                    setCcVendorName(name);
                                }}
                            />

                            <InvoiceFileDropzone
                                isCompressing={isCompressing}
                                isDragOver={isDragOver && uploadMode === 'single'}
                                fileInputRef={fileInputRef}
                                uploadedImages={uploadedImages}
                                uploadedPdf={uploadedPdf}
                                onFileChange={handleFileUpload}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onRemoveImage={removeImage}
                                onRemovePdf={removePdf}
                            />

                            {uploadInvoiceMutation.error && (
                                <Alert status="error">
                                    <AlertIcon />
                                    Failed to upload invoice. Please try again.
                                </Alert>
                            )}
                        </VStack>
                    )}

                    {uploadMode === 'bulk' && (
                        <VStack spacing={6} align="stretch">
                            <InvoiceUploadForm
                                invoiceType={invoiceType}
                                onInvoiceTypeChange={setInvoiceType}
                            />

                            <Text fontSize="sm" color="gray.600">
                                Each file is one invoice (max {WEB_BULK_UPLOAD_MAX}). JPG, PNG, TIF, or PDF.
                            </Text>

                            <Box
                                border="2px dashed"
                                borderColor={isDragOver && uploadMode === 'bulk' ? 'blue.500' : 'gray.300'}
                                borderRadius="md"
                                p={6}
                                textAlign="center"
                                cursor={isCompressing ? 'wait' : 'pointer'}
                                bg={isDragOver && uploadMode === 'bulk' ? 'blue.50' : 'transparent'}
                                opacity={isCompressing ? 0.7 : 1}
                                pointerEvents={isCompressing ? 'none' : 'auto'}
                                onClick={() => !isCompressing && bulkFileInputRef.current?.click()}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <Text fontWeight="semibold" color="gray.700">
                                    Drop files here or click to browse
                                </Text>
                                <Text fontSize="sm" color="gray.500" mt={1}>
                                    {bulkFiles.length} / {WEB_BULK_UPLOAD_MAX} selected
                                </Text>
                                <input
                                    ref={bulkFileInputRef}
                                    type="file"
                                    accept={INVOICE_UPLOAD_ACCEPT}
                                    multiple
                                    style={{ display: 'none' }}
                                    onChange={handleBulkFileUpload}
                                />
                            </Box>

                            {bulkFiles.length > 0 && (
                                <VStack align="stretch" spacing={2} maxH="240px" overflowY="auto">
                                    {bulkFiles.map((entry) => (
                                        <HStack
                                            key={entry.id}
                                            justify="space-between"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            px={3}
                                            py={2}
                                        >
                                            <Text fontSize="sm" noOfLines={1} flex={1}>
                                                {entry.file.name} ({entry.kind === 'pdf' ? 'PDF' : 'Image'})
                                            </Text>
                                            <Button
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => removeBulkFile(entry.id)}
                                                isDisabled={isBusy}
                                            >
                                                Remove
                                            </Button>
                                        </HStack>
                                    ))}
                                </VStack>
                            )}

                            {bulkUploadMutation.isPending && bulkUploadMutation.progress && (
                                <Box>
                                    <Text fontSize="sm" color="gray.600" mb={2}>
                                        {bulkUploadMutation.progress.message}
                                    </Text>
                                    <Progress
                                        value={bulkUploadMutation.progress.percent}
                                        size="sm"
                                        colorScheme="blue"
                                        borderRadius="full"
                                    />
                                </Box>
                            )}

                            {bulkUploadMutation.error && (
                                <Alert status="error">
                                    <AlertIcon />
                                    {bulkUploadMutation.error instanceof Error
                                        ? bulkUploadMutation.error.message
                                        : 'Failed to bulk upload. Please try again.'}
                                </Alert>
                            )}
                        </VStack>
                    )}

                    {uploadMode === 'email' && emailInvoicingEnabled && invoiceInboxAddresses && (
                        <VStack spacing={4} align="stretch">
                            <Box
                                borderLeftWidth="3px"
                                borderLeftColor="blue.400"
                                bg="blue.50"
                                py={2}
                                px={3}
                                borderRadius="md"
                            >
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                                    For Reliable Extraction
                                </Text>
                                <UnorderedList spacing={1} fontSize="sm" color="gray.700" pl={1} mb={0}>
                                    <ListItem>
                                        Forward vendor emails with a PDF or image (PNG/JPEG/TIF) attached to the matching
                                        inbox address below. Use a bulk address when each attachment should become its
                                        own invoice.
                                    </ListItem>
                                    <ListItem>
                                        Include the shop location in the subject when it is not your default location.
                                    </ListItem>
                                </UnorderedList>
                            </Box>

                            <InvoiceInboxAddressesTable
                                addresses={invoiceInboxAddresses}
                                showIntro={false}
                            />
                        </VStack>
                    )}
                </VStack>
            </AppDrawer>

            <AlertDialog
                isOpen={isMultiImageConfirmOpen}
                leastDestructiveRef={multiImageConfirmRef}
                onClose={onMultiImageConfirmClose}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="semibold">
                            Multiple Images Selected
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            <Text fontWeight="semibold" mb={2}>
                                Important:
                            </Text>
                            <UnorderedList spacing={1} pl={4}>
                                <ListItem>All pages must be from the <strong>same invoice</strong>. Uploading pages from different invoices will cause processing errors.</ListItem>
                                <ListItem>Pages must be in <strong>correct order</strong> (page 1, then page 2, etc.). Wrong order can break extraction and matching.</ListItem>
                                <ListItem>
                                    <strong>Fill the frame:</strong> crop or rescan so each page fills the image with
                                    minimal empty space around the document—large dead space often breaks extraction.
                                </ListItem>
                                <ListItem>
                                    <strong>Upright orientation:</strong> every photo must be right-side up, not sideways
                                    or upside down.
                                </ListItem>
                            </UnorderedList>
                            <Text mt={3}>
                                Are you sure your selection meets all of the above?
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={multiImageConfirmRef} onClick={onMultiImageConfirmClose}>
                                Cancel
                            </Button>
                            <Button colorScheme="blue" onClick={performUpload} ml={3}>
                                Yes, Same Invoice & In Order
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </>
    );
};

export default CreateInvoiceModal;
